import React, { useState, useEffect, useMemo } from 'react';
import { Route, Routes, Navigate} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { getAppTheme } from './config/theme';
import keycloak from './keycloak';
import LoginForm from './views/LoginForm/LoginForm';
import ForgetPassword from './views/ForgetPassword/ForgetPassword';
import ResetPassword from './views/ResetPassword/ResetPassword';
import PersonalDetails from './views/PersonalDetails/PersonalDetails';
import WebBanner from "./components/Banner/Web/WebBanner";
import MenuWeb from "./components/Menu/Menu";
import useScreenSize from "./components/useScreenSize";
import MobileBanner from "./components/Banner/Mobile/MobileBanner";
import DashboardPage from './components/DashboardPage/DashboardPage';
//import DashboardPageDemo from './components/DashboardPageDemo/DashboardPageDemo';
import SubmitInvoicePage from './views/SubmitInvoicePage/SubmitInvoicePage';
//import SubmitInvoicePageDemo from './views/SubmitInvoicePageDemo/SubmitInvoicePageDemo';
import InvoicePreview from './views/InvoicePreview/InvoicePreview';
import ApproverPreview from './views/ApproverPreview/ApproverPreview'; 
import RejectionNote from './views/RejectionNote/RejectionNote';
import ApproverDashboard from './views/ApproverDashboard/ApproverDashboard';
import Settings from "./views/Settings/Settings";
import MyProfile from './views/MyProfile/MyProfile';
import CompanyOverview from './views/CompanyOverview/CompanyOverview';
import ListOfUsers from './views/ListOfUsers/ListOfUsers';
import styles from "./App.module.css"
import UserDetails from './views/UserDetails/UserDetails';
import CreateUserPage from './views/CreateUser/CreateUserPage';
//import CreateUserPageDemo from './views/CreateUserDemo/CreateUserDemo';
import ApproveUsersPage from './views/ApproveUsersPage/ApproveUsersPage';
//import ApproveUsersPageDemo from './views/ApproveUsersPageDemo/ApproveUsersPageDemo';
import ApprovalSettingsPage from './views/ApprovalSettingsPage/ApprovalSettingsPage';
import AccountNotFound from './views/AccountNotFound/AccountNotFound';
import useAuth from './hooks/useAuth';
/* import ApprovalSettingsPageDemo from './views/ApprovalSettingsPageDemo/ApprovalSettingsPageDemo';
import SettingsDemo from './views/SettingsDemo/SettingsDemo';
import MyProfileDemo from './views/MyProfileDemo/MyProfileDemo';
import ListOfUsersDemo from './views/ListOfUsersDemo/ListOfUsersDemo';
import UserDetailsDemo from './views/UserDetailsDemo/UserDetailsDemo';
import ApproverDashboardDemo from './views/ApproverDashboardDemo/ApproverDashboardDemo';
import ApproverPreviewDemo from './views/ApproverPreviewDemo/ApproverPreviewDemo';
import RejectionNoteDemo from './views/RejectionNoteDemo/RejectionNoteDemo';
import CompanyOverviewDemo from './views/CompanyOverviewDemo/CompanyOverviewDemo';
import InvoicePreviewDemo from './views/InvoicePreviewDemo/InvoicePreviewDemo'; */

import { useSelector } from 'react-redux';

function App() {
  const isAuthenticated = keycloak.authenticated;
  // Called here (rather than in Menu) so the profile-fetch effect inside useAuth runs
  // immediately on mount, regardless of which branch below ends up rendering.
  const { userLogout, profileError, retryProfileFetch } = useAuth();
  const screenSize = useScreenSize();
  /* const [isDemoMode, setIsDemoMode] = useState(false);

  const onChangeDemoMode = (mode) => {
    setIsDemoMode(mode);
  }; */

  var isMobile = screenSize.width < 600 ? true : false;
  const [collapsed, setCollapsed] = useState(false);

  // Extract user type from Keycloak token
  const userType = useSelector(state => state.graphql.accessLevel);
  const profileNotFound = useSelector(state => state.graphql.profileNotFound);
  const darkMode = useSelector(state => state.graphql.darkMode);

  // Keep the <html> theme attribute (and the localStorage cache the bootstrap
  // script in index.html reads) in sync with the redux value on every change.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    try {
      localStorage.setItem('darkMode', String(darkMode));
    } catch (e) {}
  }, [darkMode]);

  // Single MUI theme for the whole tree, kept in sync with the same redux value
  // that drives the data-theme attribute above, so every MUI component (tables,
  // icon buttons, form controls) gets dark mode without wiring its own theme.
  const muiTheme = useMemo(() => getAppTheme(darkMode), [darkMode]);
  //console.log("User Access Level in App.js:", userType);
  const isApprover = userType?.startsWith('Approver');
  //console.log("Is Approver:", isApprover);
  const isManager = userType === 'Manager';
  //console.log("Is Manager:", isManager);
  // True only while we don't have a role yet AND haven't been told there's no profile.
  // The routes below only render once this is false (see the render branches), so by the
  // time allowApproverRoutes/allowManagerRoutes are evaluated, the role is always known.
  const isRoleHydrating = Boolean(isAuthenticated && !userType && !profileNotFound);
  const allowApproverRoutes = isApprover;
  const allowManagerRoutes = isManager;

  const handleToggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const handleForgetPasswordSubmit = (email) => {
    //TODO: Implement Forget Password Logic
    console.log(email);
  };

  const handleSetPasswordSubmit = (password) => {
    //TODO: Implement Set Password Logic
    console.log(password);
  };

  const handleResetPasswordSubmit = (password) => {
    //TODO: Implement Reset Password Logic
    console.log(password);
  };

  return (
    <ThemeProvider theme={muiTheme}>
    <div>
      {!isAuthenticated ? (
          <Routes>
            <Route path="/" element= {<LoginForm />}></Route>

            <Route
              path="/forget-password"
              element={<ForgetPassword onSubmit={handleForgetPasswordSubmit} />}
            ></Route>

            <Route
              path="/onboarding/:uuid"
              element={<PersonalDetails />}
            ></Route>

            <Route
              path="/reset-password"
              element={<ResetPassword onSubmit={handleResetPasswordSubmit} />}
            ></Route>

            <Route path="/logout" element= {<LoginForm />}></Route>

            <Route path ="*" element={<Navigate to = "/" />} />
          </Routes>
      ) : profileNotFound ? (
        <AccountNotFound />
      ) : isRoleHydrating && profileError ? (
        <div className={styles.loadingScreen}>
          <div>
            <p>We couldn't reach the server to load your account. Check your connection and try again.</p>
            <button onClick={retryProfileFetch}>Retry</button>
          </div>
        </div>
      ) : isRoleHydrating ? (
        <div className={styles.loadingScreen}>Loading your account...</div>
      ) : (
        <div className={`${styles.App} App`}>
            {isMobile ? <MobileBanner toggleCollapsed={handleToggleSidebar} /> : <WebBanner />}
            <div className={styles.banner}>
              <MenuWeb collapsedMobile={collapsed} onLogout={userLogout}></MenuWeb>
              <section className={styles.menuSection}>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="settings" element={<Settings />} />
                <Route path="submit-invoice" element={<SubmitInvoicePage />} />
                <Route path="submit-invoice/edit/:invoiceID" element={<SubmitInvoicePage />} />
                <Route path="logout" element={<LoginForm />} />
                <Route path="my-profile" element={<MyProfile />} />
                <Route path="details/:category/:id" element={<InvoicePreview />} />
                {allowApproverRoutes ? <Route path="approve-invoices/details/:category/:id" element={<ApproverPreview />} /> : null}
                {allowApproverRoutes ? <Route path="approve-invoices/reject/:category/:id" element={<RejectionNote />} /> : null}
                {allowApproverRoutes ? <Route path="approve-invoices" element={<ApproverDashboard />} /> : null}
                {allowApproverRoutes ? <Route path="approve-users" element={<ApproveUsersPage />}></Route> : null}
                {allowManagerRoutes ? <Route path="create-user" element={<CreateUserPage />} /> : null}
                {allowManagerRoutes ? <Route path="approval-settings" element={<ApprovalSettingsPage />} /> : null}
                {allowManagerRoutes ? <Route path="company-overview" element={<CompanyOverview />} /> : null}
                {allowManagerRoutes ? <Route path="user-management" element={<ListOfUsers />} /> : null}
                {allowManagerRoutes ? <Route path="user-management/user/:organization/:name" element={<UserDetails />} /> : null}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </section>
            </div>
          </div>
      )}
    </div>
    </ThemeProvider>
  );
}

export default App;
